<script lang="ts" setup>
import type { Product, ProductPrice } from '@/types'
import { computed, onMounted, reactive, toRaw, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotifyStore } from '@/stores'
import { listedPaidIntervals, parseToPrimitive, termPeriod, uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { formatCurrency } from '@/utils'
import { useAxios } from '@/composables/useAxios'
import { getBackendErrorCode, handleAxiosError } from '@/composables/useAxiosError'
import type { PaymentInterval } from '@/types/purchase/PaymentInterval'

const { t } = useI18n()

const props = defineProps<{
  product: Product
  isNewProduct?: boolean
}>()

const emits = defineEmits<{
  (e: 'refresh', bool: boolean): void
}>()

const data = reactive({
  prices: [] as ProductPrice[],
  showModal: false,
  newPrice: false,
  formData: {} as ProductPrice,
  saving: false,
  locale: window.navigator.language
})

// A whole cycle's own price is taken only for mail, only for a cycle the
// product lists, and only when the price is created; the backend refuses a
// changed map on an existing price.
const cycleIntervals = computed((): PaymentInterval[] =>
  props.product.features?.type === 'mail'
    ? listedPaidIntervals(props.product).filter((i) => i !== 'monthly')
    : []
)
const cycleInputs = reactive<Record<string, number | string>>({})
const cycleLabel = (interval: string) =>
  t('dashboard.admin.products.prices.modal.cycleAmount', { period: termPeriod(interval as PaymentInterval, t) })

const CYCLE_ERROR_KEYS: Record<string, string> = {
  invalid_cycle_amounts: 'dashboard.admin.products.prices.errors.invalidCycleAmounts',
  cycle_amounts_immutable: 'dashboard.admin.products.prices.errors.cycleAmountsImmutable'
}

const sortPrices = computed((): ProductPrice[] => {
  if (!data.prices) return [] as ProductPrice[]

  // data.prices is the parent's prop array, so sort a copy: sorting it in
  // place mutates the prop and re-fires the deep watcher that assigns it.
  // Default price first, everything else in its existing order.
  return [...data.prices].sort((a, b) => (b.default ? 1 : 0) - (a.default ? 1 : 0))
})

const handleNewPrice = () => {
  // Check if product has been saved to backend
  // For new products, slug is auto-generated client-side but product doesn't exist yet
  if (!props.product.slug || props.isNewProduct) {
    const notifyStore = useNotifyStore()
    notifyStore.createNotify({
      id: uuid(),
      title: t('common.error'),
      message: t('dashboard.admin.products.prices.notifications.saveFirst'),
      alertType: 'warning'
    })
    return
  }

  data.showModal = true
  data.newPrice = true
  data.formData = {} as ProductPrice
  data.formData.active = true
  data.formData.default = true
  for (const key of Object.keys(cycleInputs)) delete cycleInputs[key]
}

const handleUpdatePrice = (price: ProductPrice) => {
  data.showModal = true
  data.newPrice = false
  data.formData = price
}

const handleCloseModal = () => {
  data.showModal = false
}

const handleSave = () => {
  data.saving = true

  data.formData.amount = parseToPrimitive(data.formData.amount)
  data.formData.tax = parseToPrimitive(data.formData.tax)
  data.formData.currency = data.formData.currency?.toUpperCase()
  if (data.newPrice) {
    const own = cycleIntervals.value
      .filter((i) => cycleInputs[i] !== undefined && cycleInputs[i] !== '')
      .map((i) => [i, Number(cycleInputs[i])] as const)
    if (own.length) data.formData.cycle_amounts = Object.fromEntries(own)
  }

  // Build the price array - either add new or update existing
  let updatedPrices: ProductPrice[]
  if (data.newPrice) {
    // Adding new price - append to existing prices
    updatedPrices = [...(props.product.price || []), toRaw(data.formData)]
  } else {
    // Updating existing price - replace in array
    updatedPrices = (props.product.price || []).map((p) =>
      p.id === data.formData.id ? toRaw(data.formData) : p
    )
  }

  // Send the full product object with updated prices
  const productToSave = {
    ...toRaw(props.product),
    price: updatedPrices
  } as Product

  useAxios<Product, null>('POST', '/product', true, productToSave)
    .then((res) => {
      if (!res) return

      const notifyStore = useNotifyStore()
      if (res.status === STATUS_CODES.OK) {
        notifyStore.createNotify({
          id: uuid(),
          title: t('common.success'),
          message: t('dashboard.admin.products.prices.notifications.updated'),
          alertType: 'success'
        })
      } else if (res.status === STATUS_CODES.CREATED) {
        notifyStore.createNotify({
          id: uuid(),
          title: t('common.success'),
          message: t('dashboard.admin.products.prices.notifications.created'),
          alertType: 'success'
        })
      }
    })
    .catch((error) => {
      const key = CYCLE_ERROR_KEYS[getBackendErrorCode(error) ?? '']
      handleAxiosError(error, { logPrefix: 'PriceTable', silentStatuses: key ? [400] : [] })
      if (key) {
        useNotifyStore().createNotify({ id: uuid(), title: t('common.error'), message: t(key), alertType: 'error' })
      }
    })
    .finally(() => {
      data.saving = false
      data.showModal = false
      emits('refresh', true)
    })
}

onMounted(() => {
  if (props.product.price) {
    data.prices = props.product.price as ProductPrice[]
  }
})

watch(
  () => props.product.price,
  (newPrices) => {
    if (newPrices) {
      data.prices = newPrices as ProductPrice[]
    }
  },
  { deep: true }
)
</script>

<template>
  <div v-if="product" class="price-table-container">
    <!-- Add Price Button -->
    <button class="add-price-btn" @click.prevent="handleNewPrice">
      <v-icon icon="mdi-plus" size="18" />
      <span>{{ $t('dashboard.admin.products.prices.newPrice') }}</span>
    </button>

    <!-- Price List -->
    <div class="price-list">
      <div v-if="sortPrices.length === 0" class="empty-state">
        <v-icon icon="mdi-currency-eur-off" size="40" />
        <span>{{ $t('dashboard.admin.products.prices.empty') }}</span>
      </div>

      <div
        v-for="price in sortPrices"
        :key="price.id"
        class="price-item"
        @click.prevent="handleUpdatePrice(price)"
      >
        <div class="price-main">
          <span class="price-amount">
            {{ formatCurrency((price.amount || 0) / 100, price.currency || 'eur', data.locale) }}
          </span>
          <div class="price-badges">
            <span v-if="price.default" class="badge badge-default">{{ $t('dashboard.admin.products.prices.standard') }}</span>
            <span class="badge" :class="price.active ? 'badge-active' : 'badge-inactive'">
              {{ price.active ? $t('dashboard.admin.products.prices.active') : $t('dashboard.admin.products.prices.inactive') }}
            </span>
          </div>
        </div>
        <div class="price-details">
          <span v-if="price.description" class="price-desc">{{ price.description }}</span>
          <div class="price-meta">
            <span class="meta-item">
              <v-icon icon="mdi-cash" size="14" />
              {{ price.currency?.toUpperCase() || 'EUR' }}
            </span>
            <span class="meta-item">
              <v-icon icon="mdi-percent" size="14" />
              {{ ((price.tax ?? 0) * 100).toFixed(0) }}% MwSt.
            </span>
            <span v-for="(cents, interval) in price.cycle_amounts" :key="interval" class="meta-item" data-test="price-cycle-amount">
              <v-icon icon="mdi-calendar-sync-outline" size="14" />
              {{ cycleLabel(String(interval)) }}: {{ cents }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="data.showModal" class="modal-overlay" @click.self="handleCloseModal">
          <div class="modal-container">
            <div class="modal-header">
              <div class="modal-title-section">
                <div class="modal-icon">
                  <v-icon :icon="data.newPrice ? 'mdi-plus' : 'mdi-pencil'" size="20" />
                </div>
                <h3 class="modal-title">{{ data.newPrice ? $t('dashboard.admin.products.prices.modal.newPrice') : $t('dashboard.admin.products.prices.modal.editPrice') }}</h3>
              </div>
              <button class="modal-close" @click="handleCloseModal">
                <v-icon icon="mdi-close" size="20" />
              </button>
            </div>

            <div class="modal-body">
              <!-- Status Toggles -->
              <div class="modal-toggles">
                <label class="toggle-item">
                  <input
                    type="checkbox"
                    v-model="data.formData.active"
                    class="toggle-input"
                  />
                  <span class="toggle-switch"></span>
                  <span class="toggle-label">{{ $t('dashboard.admin.products.prices.modal.active') }}</span>
                </label>

                <label class="toggle-item">
                  <input
                    type="checkbox"
                    v-model="data.formData.default"
                    class="toggle-input"
                  />
                  <span class="toggle-switch"></span>
                  <span class="toggle-label">{{ $t('dashboard.admin.products.prices.modal.standard') }}</span>
                </label>
              </div>

              <!-- Form Fields -->
              <div class="modal-form">
                <div class="form-group">
                  <label class="form-label">{{ $t('dashboard.admin.products.prices.modal.amount') }}</label>
                  <input
                    v-model="data.formData.amount"
                    type="number"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.products.prices.modal.amountPlaceholder')"
                    :autofocus="data.newPrice"
                    :readonly="!data.newPrice"
                  />
                  <span class="form-hint">{{ $t('dashboard.admin.products.prices.modal.amountHint') }}</span>
                </div>

                <div class="form-group">
                  <label class="form-label">{{ $t('dashboard.admin.products.prices.modal.currency') }}</label>
                  <input
                    v-model="data.formData.currency"
                    type="text"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.products.prices.modal.currencyPlaceholder')"
                    maxlength="3"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">{{ $t('dashboard.admin.products.prices.modal.tax') }}</label>
                  <input
                    v-model="data.formData.tax"
                    type="number"
                    step="0.01"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.products.prices.modal.taxPlaceholder')"
                  />
                  <span class="form-hint">{{ $t('dashboard.admin.products.prices.modal.taxHint') }}</span>
                </div>

                <template v-if="data.newPrice">
                  <div v-for="interval in cycleIntervals" :key="interval" class="form-group">
                    <label class="form-label">{{ cycleLabel(interval) }}</label>
                    <input
                      v-model="cycleInputs[interval]"
                      type="number"
                      class="form-input"
                      :data-test="`cycle-amount-${interval}`"
                    />
                    <span class="form-hint">{{ $t('dashboard.admin.products.prices.modal.cycleAmountHint') }}</span>
                  </div>
                </template>
                <template v-else>
                  <div v-for="(cents, interval) in data.formData.cycle_amounts" :key="interval" class="form-group">
                    <label class="form-label">{{ cycleLabel(String(interval)) }}</label>
                    <input :value="cents" type="number" class="form-input" readonly :data-test="`cycle-amount-${interval}`" />
                  </div>
                </template>

                <div class="form-group full-width">
                  <label class="form-label">{{ $t('dashboard.admin.products.prices.modal.description') }}</label>
                  <input
                    v-model="data.formData.description"
                    type="text"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.products.prices.modal.descriptionPlaceholder')"
                  />
                </div>
              </div>
            </div>

            <div class="modal-footer">
              <button class="btn-cancel" @click="handleCloseModal">
                {{ $t('dashboard.admin.products.prices.modal.cancel') }}
              </button>
              <button
                class="btn-save"
                :disabled="data.saving"
                @click.prevent="handleSave"
              >
                <v-progress-circular v-if="data.saving" indeterminate size="16" width="2" />
                <v-icon v-else icon="mdi-check" size="18" />
                <span>{{ $t('dashboard.admin.products.prices.modal.save') }}</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.price-table-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.add-price-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  background: rgba(76, 175, 80, 0.08);
  border: 1px dashed rgba(76, 175, 80, 0.3);
  border-radius: 10px;
  color: #4CAF50;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(76, 175, 80, 0.12);
    border-style: solid;
  }
}

.price-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px;
  color: rgba(200, 218, 255, 0.4);

  .v-icon {
    opacity: 0.5;
  }

  span {
    font-size: 0.85rem;
  }
}

.price-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(120, 163, 255, 0.2);
  }
}

.price-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.price-amount {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.price-badges {
  display: flex;
  gap: 6px;
}

.badge {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.badge-default {
  background: rgba(0, 176, 255, 0.15);
  color: #00B0FF;
}

.badge-active {
  background: rgba(76, 175, 80, 0.15);
  color: #4CAF50;
}

.badge-inactive {
  background: rgba(244, 67, 54, 0.1);
  color: #F44336;
}

.price-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.price-desc {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.price-meta {
  display: flex;
  gap: 16px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);

  .v-icon {
    opacity: 0.6;
  }
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
}

.modal-container {
  width: 100%;
  max-width: 480px;
  margin: 20px;
  background: rgba(20, 30, 55, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.modal-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 10px;

  .v-icon {
    color: #00B0FF;
  }
}

.modal-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.6);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
  }
}

.modal-body {
  padding: 24px;
}

.modal-toggles {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.toggle-input {
  display: none;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 12px;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 16px;
    height: 16px;
    background: rgba(200, 218, 255, 0.5);
    border-radius: 50%;
    transition: all 0.2s ease;
  }
}

.toggle-input:checked + .toggle-switch {
  background: rgba(76, 175, 80, 0.2);
  border-color: rgba(76, 175, 80, 0.4);

  &::after {
    left: 23px;
    background: #4CAF50;
  }
}

.toggle-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.modal-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  row-gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.full-width {
    grid-column: 1 / -1;
  }
}

.form-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.7);
}

.form-input {
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  transition: all 0.2s ease;
  width: 100%;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgba(0, 176, 255, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }

  &:read-only {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Hide number input spinners */
  &[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}

.form-hint {
  font-size: 0.7rem;
  color: rgba(200, 218, 255, 0.4);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
  background: rgba(0, 0, 0, 0.1);
}

.btn-cancel {
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
  }
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 8px;
  color: #4CAF50;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.2);
    border-color: rgba(76, 175, 80, 0.4);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

/* Modal Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(10px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .add-price-btn,
  .price-item,
  .modal-close,
  .toggle-switch,
  .toggle-switch::after,
  .form-input,
  .btn-cancel,
  .btn-save {
    transition: none;
  }

  .modal-enter-active,
  .modal-leave-active,
  .modal-enter-active .modal-container,
  .modal-leave-active .modal-container {
    transition: none;
  }
}
</style>
